import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, UseQueryResult } from 'react-query';

import { AnalyzeContext as AnalyzeContextV2 } from '../../app-v2/context/analyze-context';
import { SearchContext as SearchContextV2 } from '../../app-v2/context/search-context';
import { useTabManager } from '../../app-v2/context/tab-manager-context';
import { usePlantFolderIndex } from '../../app-v2/hooks/use-folder-tree-index';
import { EnsembleContext } from '../context/ensemble-context';
import { getEarliestBatch, getLatestBatch, getOpModes } from '../services/folder';
import { deployedModels } from '../services/models';
import { anomalousModelTags, anomalousTags } from '../services/tag';

import { useTenants } from './tenants';

import { useDebounce } from '@controlrooms/hooks';
import {
  AnomalousTagsPayload,
  AnomalySort,
  CRFolderTags,
  DeployedModelsPayload,
  FlattenedFolderTag,
  MatchType,
  Model,
  ModelAnomalousTagsPayload,
  OpMode,
  ParentFolder,
  ParentTag,
  StreamingTimeRange,
  SubSystem,
  SubSystemParentRef,
  System,
  Tag,
  TimeThreshold,
} from '@controlrooms/models';
import {
  indexedFolderToSubSystem,
  indexedTagToTag,
  getAllDescendantFolders as indexGetAllDescendantFolders,
  getFolderHierarchy as indexGetFolderHierarchy,
  getFolderPath as indexGetFolderPath,
  getReadableFolderPath as indexGetReadableFolderPath,
  getTagByName as indexGetTagByName,
  ObjectUtils,
} from '@controlrooms/utils';
import { IntervalUtil } from '@controlrooms/utils/src/chart-utils/interval';

export const FOLDER_KEY = 'PLANT_FOLDERS';
export const LIMIT_FOLDER_KEY = 'LIMIT_FOLDERS';

export enum FolderSort {
  DEFAULT,
  ALPHA_ASC_NAME,
  ALPHA_DESC_NAME,
  ALPHA_ASC_DESCRIPTION,
  ALPHA_DESC_DESCRIPTION,
}

export const useSearchStrings = (tags: Tag[], searchTerm: string) => {
  const [filteredStrings, setFilteredStrings] = useState<Tag[]>([]);
  const debouncedSearchTerm = useDebounce(searchTerm, 300); // 300ms delay

  useEffect(() => {
    if (!debouncedSearchTerm) {
      setFilteredStrings(tags);
    } else {
      const lowercasedTerm = debouncedSearchTerm.toLowerCase();
      const result = (tags || []).filter(
        (tag: Tag) =>
          tag?.name?.toLowerCase().includes(lowercasedTerm) ||
          tag?.description?.toLowerCase()?.includes(lowercasedTerm) ||
          tag?.uom?.toLowerCase()?.includes(lowercasedTerm),
      );
      setFilteredStrings(result);
    }
  }, [debouncedSearchTerm, tags]);

  return filteredStrings;
};

export const folderSorter = (sort: FolderSort) => {
  if (sort === FolderSort.DEFAULT) return undefined;
  return (a: { name: string; description: string }, b: { name: string; description: string }) => {
    if (sort === FolderSort.ALPHA_ASC_NAME) return a.name.localeCompare(b.name);
    if (sort === FolderSort.ALPHA_DESC_NAME) return b.name.localeCompare(a.name);
    if (sort === FolderSort.ALPHA_ASC_DESCRIPTION)
      return a.description.localeCompare(b.description);
    return b.description.localeCompare(a.description);
  };
};

export interface FlattenedFolder {
  type: 'root' | 'subfolder';
  folder: SubSystem;
  rootFolderId?: number;
}

export const flattenFolderTags = (
  folders: SubSystem[],
  parentId: number | undefined,
  level: number,
  isParentVisible: boolean,
  collapsedFolders: Set<number>,
): FlattenedFolderTag[] => {
  const result: FlattenedFolderTag[] = [];

  folders.forEach((folder) => {
    const isCollapsed = collapsedFolders.has(folder.folder);
    const isVisible = isParentVisible; // Folders should always be visible, only descendants hidden

    // Push folder itself (it should always be visible even if collapsed)
    result.push({
      type: parentId === undefined ? 'root' : 'folder',
      item: folder,
      parentId,
      level,
      isVisible,
    });

    // Add tags of the folder itself (both for branch and leaf nodes)
    if (isVisible && folder.tags && folder.tags.length > 0) {
      folder.tags.forEach((tag) => {
        result.push({
          type: 'tag',
          item: tag,
          parentId: folder.folder,
          level: level + 1,
          isVisible, // Tags are visible if the folder is visible
        });
      });
    }

    // Recursively flatten subfolders
    if (!isCollapsed && folder.subfolders && folder.subfolders.length > 0) {
      result.push(
        ...flattenFolderTags(
          folder.subfolders,
          folder.folder,
          level + 1,
          isVisible && !isCollapsed, // Descendants are visible only if the folder is expanded
          collapsedFolders,
        ),
      );
    }
  });

  return result;
};

const sortFolders = (folderData: Array<CRFolderTags> | undefined, subsystemIds: Array<number>) => {
  if (!folderData) return folderData;
  return [...folderData].sort((a, b) =>
    subsystemIds.indexOf(a.folder) > subsystemIds.indexOf(b.folder) ? 1 : -1,
  );
};

function isSubSystem(folder: System<SubSystem> | SubSystem): folder is SubSystem {
  return 'tags' in folder;
}

export const useFilteredFolders = (
  subfolders: System<SubSystem>[] | SubSystem[],
): System<SubSystem>[] | SubSystem[] => {
  const searchContextV2 = useContext(SearchContextV2);
  const { isSearchWithin, searchTerm, setSearchCount, matchType } = searchContextV2;
  const { pinnedTags: V2PinnedTags, selectedFolders: V2SelectedFolders } =
    useContext(AnalyzeContextV2);
  const pinnedTags = V2PinnedTags;
  const selectedFolders: number[] = V2SelectedFolders;
  const _searchTerm = searchTerm.toLowerCase();
  const totalCount = useRef(0);

  const filterMatchString = useCallback(
    (tag: Tag) => {
      const tagNameDescription = `${tag?.tag_display_name || ''} ${tag?.description || ''} ${
        tag?.uom || ''
      }`.toLowerCase();
      const searchTermArray = _searchTerm.trim().split(' ');
      if (matchType === MatchType.ANY) {
        return searchTermArray.some((el) => {
          return tagNameDescription.includes(el);
        });
      } else {
        return searchTermArray.every((el) => {
          return tagNameDescription.includes(el);
        });
      }
    },
    [_searchTerm, matchType],
  );

  const filterFolders = useCallback(
    (folder: SubSystem & { parentPath?: string }, parentPath?: string) => {
      let matches = false;

      // Check folder name and description matches
      if (
        folder?.name.toLowerCase().includes(_searchTerm) ||
        folder?.infra_display_name.toLowerCase().includes(_searchTerm) ||
        (folder?.description && folder?.description.toLowerCase().includes(_searchTerm))
      ) {
        matches = true;
        totalCount.current++; // Increment for each matching folder
      }

      // Filter tags if the folder is a SubSystem
      const isSubfolder = isSubSystem(folder);
      const tags =
        isSubfolder && folder.tags
          ? (folder.tags as (Tag & { parentPath: string; pinned?: boolean })[])
              .filter((tag) => {
                if (isSearchWithin) {
                  return pinnedTags.some((pinnedTag) => {
                    return (
                      pinnedTag.tag_display_name === tag?.tag_display_name &&
                      pinnedTag?.folder === folder?.folder
                    );
                  });
                } else {
                  return tag;
                }
              })
              .filter((tag) => {
                let isPinnedTag = false;
                if (isSearchWithin) {
                  isPinnedTag = pinnedTags.some((pinnedTag) => {
                    return filterMatchString(pinnedTag);
                  });
                }
                parentPath && (tag.parentPath = parentPath);

                return filterMatchString(tag) && !!(isSearchWithin ? isPinnedTag : true);
              })
          : [];

      parentPath && (folder.parentPath = parentPath);

      if (tags.length > 0) {
        matches = true;
        totalCount.current += tags.length; // Add the number of matching tags
      }

      // Recursively filter subfolders
      const filteredSubfolders = folder.subfolders
        .map((subfolder) =>
          filterFolders(subfolder, `${parentPath ? parentPath + ' / ' : ''}${folder.description}`),
        )
        .filter((subfolder) => subfolder !== null)
        .filter((subfolder) => {
          if (isSearchWithin && subfolder) {
            return Boolean(
              selectedFolders?.includes(subfolder?.folder) ||
                subfolder?.subfolders?.length ||
                subfolder?.tags?.length,
            );
          }
          return true;
        }) as typeof folder.subfolders;

      if (filteredSubfolders.length > 0) {
        matches = true;
      }

      if (matches) {
        return { ...folder, tags: isSubSystem(folder) ? tags : [], subfolders: filteredSubfolders };
      }

      return null;
    },
    [_searchTerm, filterMatchString, isSearchWithin, pinnedTags, selectedFolders],
  );

  const isEligibleForSearch = useCallback(() => {
    if (matchType === MatchType.ANY) {
      return (
        searchTerm &&
        searchTerm
          .trim()
          .split(' ')
          .every((s) => s.length > 0)
      );
    } else {
      return searchTerm && searchTerm.trim().length > 0;
    }
  }, [matchType, searchTerm]);

  // Use useMemo to avoid recalculating unless subfolders or searchTerm changes
  return useMemo(() => {
    totalCount.current = 0;

    const results = isEligibleForSearch()
      ? (subfolders as SubSystem[])
          .map((subfolder) => filterFolders(subfolder))
          .filter((subfolder) => subfolder !== null)
      : [];
    setSearchCount(totalCount.current); // Update context with final count
    return results as System<SubSystem>[] | SubSystem[];
  }, [isEligibleForSearch, subfolders, setSearchCount, filterFolders]);
};

export const useFlatFolders = (
  _sort: FolderSort,
  filter?: {
    folderId?: number;
  },
) => {
  const { index, isLoading, error } = usePlantFolderIndex();

  const flat = useMemo(() => {
    if (!index) return undefined;

    const result: ParentFolder[] = [];
    index.foldersById.forEach((folder, folderId) => {
      // Skip the plant root
      if (folderId === index.plantRootId) return;

      const parentId = index.parentMap.get(folderId) ?? index.plantRootId ?? -1;

      // Build tags with ParentTag for this folder
      const tagKeys = index.tagKeysByFolderId.get(folderId) ?? [];
      const tags = tagKeys
        .map((key) => {
          const t = index.tagsByKey.get(key);
          return t ? new ParentTag(indexedTagToTag(t), folderId) : undefined;
        })
        .filter((t): t is ParentTag => t !== undefined);

      result.push({
        folder: folderId,
        name: folder.name,
        infra_display_name: folder.infraDisplayName,
        description: folder.description,
        has_model: folder.hasModel,
        has_limits: folder.hasLimits,
        subfolders: [],
        tags,
        parentFolder: parentId,
        infra_type_id: folder.infraTypeId,
      } as ParentFolder);
    });

    if (filter?.folderId) {
      return result.filter((f) => f.folder === filter.folderId);
    }

    return result;
  }, [index, filter?.folderId]);

  return { data: flat, isLoading, isError: !!error, error };
};

export const ANOMALOUS_FOLDERS_KEY = 'anomalous_folders';
export const useAnomalousTags = (
  { startTime, endTime, streamingTimeInSeconds }: StreamingTimeRange,
  selectedFolders: number[],
  sort: AnomalySort = AnomalySort.SEVERITY,
  showAnalyzeLimits?: boolean,
) => {
  const { currentTenant } = useTenants();
  const { pinnedTags = [] } = useContext(AnalyzeContextV2);
  const { activeTabId, activeViewState } = useTabManager();
  const viewState = activeViewState;
  const viewEnsemble = viewState?.selectedEnsemble;
  const { selectedEnsemble: appEnsemble } = useContext(EnsembleContext);
  const selectedEnsemble = viewEnsemble ?? appEnsemble;
  const tenantConfig = currentTenant?.preferences;

  let interval = IntervalUtil.DEFAULT_INTERVAL;
  if (startTime && endTime) {
    interval = IntervalUtil.fromThresholds(
      startTime,
      endTime,
      tenantConfig?.analyzeTimeThresholds as Array<TimeThreshold>,
    );
  }

  const isStreaming = Boolean(streamingTimeInSeconds);
  const formattedStartTime = startTime?.toISOString() ?? undefined;
  const formattedEndTime = endTime?.toISOString() ?? undefined;

  const payload = {
    folders: selectedFolders.length ? selectedFolders : undefined,
    sort_order: sort,
    show_limits: showAnalyzeLimits,
  } as AnomalousTagsPayload;

  if (isStreaming) {
    payload.preset = streamingTimeInSeconds;
  } else {
    payload.start_time = formattedStartTime;
    payload.end_time = formattedEndTime;
  }

  if (selectedEnsemble?.family_id) {
    payload.ensemble_family_id = selectedEnsemble?.family_id;
  }

  // Generate query key with viewId if available
  const queryKeyWithViewId = activeTabId
    ? `${ANOMALOUS_FOLDERS_KEY}-${activeTabId}`
    : ANOMALOUS_FOLDERS_KEY;

  const { data, ...rest } = useQuery(
    [queryKeyWithViewId, ANOMALOUS_FOLDERS_KEY, payload],
    () => anomalousTags(payload, selectedEnsemble?.family_id as string),
    {
      select: (r) => r.result,
      enabled: ObjectUtils.areNonNull(
        payload as unknown as Record<string, unknown>,
        Object.keys(payload),
      ),
      retry: isStreaming ? 1 : undefined,
      refetchInterval: isStreaming ? IntervalUtil.refetchMsFromInterval(interval) : false,
    },
  );

  const { index: folderIndex } = usePlantFolderIndex();
  const subsystemIds = useMemo(() => {
    if (!folderIndex) return [];
    const ids: number[] = [];
    folderIndex.foldersById.forEach((folder, folderId) => {
      if (folderId === folderIndex.plantRootId) return;
      if (folder.hasModel || folder.hasLimits) ids.push(folderId);
    });
    return ids;
  }, [folderIndex]);

  const sortedData = useMemo(() => {
    if (!data || !subsystemIds?.length) return data;
    return sortFolders(data, subsystemIds);
  }, [data, subsystemIds]);

  const merged = useMemo(() => {
    const pinnedMap = pinnedTags.reduce<Record<number, string[]>>((acc, { folder, name }) => {
      if (acc[folder]) {
        acc[folder].push(name);
      } else {
        acc[folder] = [name];
      }
      return acc;
    }, {});

    if (!sortedData && pinnedMap !== null && pinnedMap !== undefined) {
      // Return pinned only
      return Object.entries(pinnedMap).map(([folder, tags]) => ({
        folder: Number(folder),
        tags,
      }));
    }

    const pinnedFolders = pinnedMap ? Object.keys(pinnedMap).map((f) => Number(f)) : [];

    // Merge pinned with data
    const _data = structuredClone(sortedData) as CRFolderTags[];
    let idx = 0;
    pinnedFolders.forEach((f) => {
      const found = _data.find((d) => d.folder === f);
      if (found) {
        found.tags = [...pinnedMap[f], ...found.tags.filter((t) => !pinnedMap[f].includes(t))];
      } else {
        _data.splice(idx++, 0, { folder: f, tags: pinnedMap[f] });
      }
    });

    return _data;
  }, [sortedData, pinnedTags]);

  return { data: merged, ...rest, isSuccess: rest.isSuccess || merged?.length } as UseQueryResult<
    CRFolderTags[]
  >;
};

export const ANOMALOUS_MODEL_TAGS_KEY = 'anomalous_model_tags';
export const useAnomalousModelTags = (
  { startTime, endTime, streamingTimeInSeconds }: StreamingTimeRange,
  selectedFolders: number[],
  modelIds: number[],
  sort: AnomalySort = AnomalySort.SEVERITY,
  showAnalyzeLimits?: boolean,
) => {
  const { currentTenant } = useTenants();
  const { activeTabId, activeViewState } = useTabManager();
  const viewState = activeViewState;
  const viewEnsemble = viewState?.selectedEnsemble;
  const { selectedEnsemble: appEnsemble } = useContext(EnsembleContext);
  const selectedEnsemble = viewEnsemble ?? appEnsemble;
  const tenantConfig = currentTenant?.preferences;

  let interval = IntervalUtil.DEFAULT_INTERVAL;
  if (startTime && endTime) {
    interval = IntervalUtil.fromThresholds(
      startTime,
      endTime,
      tenantConfig?.analyzeTimeThresholds as Array<TimeThreshold>,
    );
  }

  const isStreaming = Boolean(streamingTimeInSeconds);
  const formattedStartTime = startTime?.toISOString() ?? undefined;
  const formattedEndTime = endTime?.toISOString() ?? undefined;

  const payload = {
    folders: selectedFolders.length ? selectedFolders : undefined,
    model_ids: modelIds,
    sort_order: sort,
    show_limits: showAnalyzeLimits,
  } as ModelAnomalousTagsPayload;

  if (isStreaming) {
    payload.preset = streamingTimeInSeconds;
  } else {
    payload.start_time = formattedStartTime;
    payload.end_time = formattedEndTime;
  }

  if (selectedEnsemble?.family_id) {
    payload.ensemble_family_id = selectedEnsemble?.family_id;
  }

  // Generate query key with viewId if available
  const queryKeyWithViewId = activeTabId
    ? `${ANOMALOUS_MODEL_TAGS_KEY}-${activeTabId}`
    : ANOMALOUS_MODEL_TAGS_KEY;

  return useQuery(
    [queryKeyWithViewId, ANOMALOUS_MODEL_TAGS_KEY, payload],
    () => anomalousModelTags(payload, selectedEnsemble?.family_id as string),
    {
      select: (r) => r.result || [],
      enabled:
        modelIds.length > 0 &&
        ObjectUtils.areNonNull(payload as unknown as Record<string, unknown>, Object.keys(payload)),
      retry: isStreaming ? 1 : undefined,
      refetchInterval: isStreaming ? IntervalUtil.refetchMsFromInterval(interval) : false,
    },
  );
};

export const DEPLOYED_MODELS_KEY = 'deployed_models';
export const useDeployedModels = (
  selectedFolders: number[],
  selectedTags: { tag_name: string; process_id: number }[],
  ensembleFamilyId: string,
  modelType?: 'UNIVARIATE' | 'MULTIVARIATE',
  refetch?: boolean,
  enabled?: boolean,
) => {
  const { activeTabId, activeViewState } = useTabManager();
  const viewState = activeViewState;

  const payload: DeployedModelsPayload = {
    effectivity_start: viewState.timeSelection.startTime.toISOString(),
    effectivity_end: viewState.timeSelection.endTime.toISOString(),
    folders: selectedFolders,
    tags: selectedTags,
    ensemble_family_id: ensembleFamilyId,
    ...(modelType && { model_type: modelType }),
  };

  // Generate query key with viewId if available
  const queryKeyWithViewId = activeTabId
    ? `${DEPLOYED_MODELS_KEY}-${activeTabId}`
    : DEPLOYED_MODELS_KEY;

  const { data, ...rest } = useQuery(
    [queryKeyWithViewId, DEPLOYED_MODELS_KEY, payload],
    () => deployedModels(payload),
    {
      select: (r) => r.result || [],
      enabled:
        enabled &&
        ObjectUtils.areNonNull(payload as unknown as Record<string, unknown>, Object.keys(payload)),
      cacheTime: 5 * 60 * 1000, // 5 minutes cache
      staleTime: 2 * 60 * 1000, // 2 minutes stale time
      keepPreviousData: true, // Keep previous data while fetching new data
    },
  );

  return { data: data || [], ...rest } as UseQueryResult<Model[]>;
};

export const useSystemsById = () => {
  const { index, isLoading, error } = usePlantFolderIndex();

  const byId = useMemo(() => {
    if (!index) return {} as Record<number, System<SubSystem>>;
    const result: Record<number, System<SubSystem>> = {};
    // Only include root-level systems (direct children of plant root)
    index.rootFolderIds.forEach((rootId) => {
      const folder = index.foldersById.get(rootId);
      if (!folder) return;
      result[rootId] = {
        folder: rootId,
        name: folder.name,
        infra_display_name: folder.infraDisplayName,
        description: folder.description,
        has_model: folder.hasModel,
        has_limits: folder.hasLimits,
        subfolders: [],
      } as System<SubSystem>;
    });
    return result;
  }, [index]);

  return { isLoading, isError: !!error, error, data: byId };
};

export const useSubSystemsById = () => {
  const { index, isLoading, error } = usePlantFolderIndex();
  const byId = useMemo(() => {
    if (!index) return {} as Record<number, SubSystemParentRef>;
    const result: Record<number, SubSystemParentRef> = {};
    index.foldersById.forEach((folder, folderId) => {
      if (folderId === index.plantRootId) return;
      const parentId = index.parentMap.get(folderId);
      const parentFolder = parentId !== undefined ? index.foldersById.get(parentId) : undefined;
      result[folderId] = {
        folder: folder.folderId,
        name: folder.name,
        infra_display_name: folder.infraDisplayName,
        description: folder.description,
        has_model: folder.hasModel,
        has_limits: folder.hasLimits,
        subfolders: [],
        tags: [],
        infra_type_id: folder.infraTypeId,
        parentName: parentFolder?.infraDisplayName,
        parentId: parentId,
      } as SubSystemParentRef;
    });
    return result;
  }, [index]);
  return { isLoading, isError: !!error, error, data: byId };
};

export const useSubsystem = (subsystem: number) => {
  const { data: byId, ...rest } = useSubSystemsById();

  return { ...rest, data: byId?.[subsystem] };
};

export const useTagsByName = () => {
  const { index, isLoading, error } = usePlantFolderIndex();
  const tagsByName = useMemo(() => {
    if (!index) return {} as Record<string, Tag>;
    const tagMap: Record<string, Tag> = {};
    index.tagsByName.forEach((tags, name) => {
      if (tags.length > 0) {
        // Legacy compat: returns first match per name. Use index.tagsByName directly for all matches.
        tagMap[name] = indexedTagToTag(tags[0]);
      }
    });
    return tagMap;
  }, [index]);

  const getTag = useCallback(
    (tagName: string, folder?: number): Tag[] => {
      if (!index) return [];
      return indexGetTagByName(index, tagName, folder).map(indexedTagToTag);
    },
    [index],
  );

  const getFolderPath = useCallback(
    (targetFolderId: number): number[] => {
      if (!index) return [];
      return indexGetFolderPath(index, targetFolderId);
    },
    [index],
  );

  const getReadableFolderPath = useCallback(
    (targetFolderId: number): string[] => {
      if (!index) return [];
      return indexGetReadableFolderPath(index, targetFolderId);
    },
    [index],
  );

  const getFolderHierarchy = useCallback(
    (targetFolderId: number): SubSystem[] => {
      if (!index) return [];
      return indexGetFolderHierarchy(index, targetFolderId).map((f) =>
        indexedFolderToSubSystem(f, index),
      );
    },
    [index],
  );

  const getAllDescendantFolders = useCallback(
    (targetFolderId: number): Set<number> => {
      if (!index) return new Set();
      return new Set(indexGetAllDescendantFolders(index, targetFolderId));
    },
    [index],
  );

  return {
    isLoading,
    isError: !!error,
    error,
    data: tagsByName,
    getTag,
    getFolderPath,
    getReadableFolderPath,
    getFolderHierarchy,
    getAllDescendantFolders,
  };
};

export const useFolderTagsByLimit = () => {
  const { index, isLoading, error } = usePlantFolderIndex();
  const folderTags = useMemo(() => {
    if (!index) return {} as Record<number, Tag[]>;
    const result: Record<number, Tag[]> = {};
    index.tagKeysByFolderId.forEach((tagKeys, folderId) => {
      result[folderId] = tagKeys
        .map((key) => {
          const t = index.tagsByKey.get(key);
          return t ? indexedTagToTag(t) : undefined;
        })
        .filter((t): t is Tag => t !== undefined);
    });
    return result;
  }, [index]);
  return { isLoading, isError: !!error, error, data: folderTags };
};

export const useLatestData = (isBatch: boolean | undefined, hasStatus: boolean | undefined) =>
  useQuery('data-latest', getLatestBatch, {
    enabled: Boolean(isBatch && hasStatus),
    select: ({ result }) => result,
  });

export const useEarliestData = (isBatch: boolean | undefined, hasStatus: boolean | undefined) =>
  useQuery('data-earliest', getEarliestBatch, {
    enabled: Boolean(isBatch && hasStatus),
    select: ({ result }) => result,
  });

export const useOpModes = (folderId: number) => {
  return useQuery(`OP_MODES_${folderId}`, () => getOpModes(folderId), {
    select: (body) => body.result,
    cacheTime: 5 * 60 * 1000,
    staleTime: 2 * 60 * 1000,
  });
};

export const useOpModesByName = (folderId: number) => {
  const { data: opModes, ...rest } = useOpModes(folderId);

  const opModesByName = useMemo(() => {
    if (!opModes || !Array.isArray(opModes)) return {} as Record<string, OpMode>;

    return opModes.reduce((acc: Record<string, OpMode>, opMode) => {
      if (opMode?.op_mode_tag) {
        acc[opMode.op_mode_tag] = opMode;
      }
      return acc;
    }, {});
  }, [opModes]);

  return { ...rest, data: opModesByName };
};
